import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { CbuttonProps } from './buttonTypes';

enum ButtonActivationKey {
  Enter = 'Enter',
  Space = ' ',
}

interface ResolveIsInteractiveInput {
  onClick?: CbuttonProps['onClick'];
  /** A link is interactive on its own: navigation is the handler. */
  href?: CbuttonProps['href'];
  disabled: boolean;
}

interface HandleButtonActivationKeyDownInput {
  event: ReactKeyboardEvent<HTMLDivElement>;
  onActivate?: () => void;
}

interface ResolveClickAnimationInput {
  isInteractive: boolean;
  disableHoverEffects: boolean;
  disableClickMotion: boolean;
}

export function resolveIsInteractive({
  onClick,
  href,
  disabled,
}: ResolveIsInteractiveInput): boolean {
  return (Boolean(onClick) || Boolean(href)) && !disabled;
}

export function resolveClickAnimation({
  isInteractive,
  disableHoverEffects,
  disableClickMotion,
}: ResolveClickAnimationInput): boolean {
  return isInteractive && !disableHoverEffects && !disableClickMotion;
}

// Keyboard activation for the div-based button surface: Enter/Space activate
// exactly like a native <button>, so role="button" is honest for AT users.
// Only the focused wrapper itself activates — keydowns bubbling up from a
// nested interactive child (e.g. a text input) must keep their default.
export function handleButtonActivationKeyDown({
  event,
  onActivate,
}: HandleButtonActivationKeyDownInput): void {
  if (!onActivate) return;
  if (event.target !== event.currentTarget) return;
  if (event.repeat) return;
  if (
    event.key !== ButtonActivationKey.Enter
    && event.key !== ButtonActivationKey.Space
  ) {
    return;
  }
  event.preventDefault();
  onActivate();
}
